import {
  Button,
  Field,
  Form,
  InputGroup,
  Text,
  Tooltip,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

export type CanvasMode = 'inspect' | 'rotate';

/**
 * The single floating context row at the top of the map: roof picker, scoped
 * Repack, and the selected roof's canvas actions. A rotation preview replaces
 * the row with the angle editor until it is applied or cancelled.
 */
const CanvasEditingBar: FC<{
  scope: string;
  /** Roof picker and arrows, hidden while the angle editor is open. */
  scopeControl?: ReactNode;
  /** A roof or panel group is the target: offer Rotate array and Roof actions. */
  hasTarget?: boolean;
  mode: CanvasMode;
  pending: boolean;
  angle: number;
  /** Anchored angles are saved intent for Repack, not a cancellable preview. */
  referenceAngle: boolean;
  focusEnabled?: boolean;
  focusIdentity?: number | null;
  panelsSelected: boolean;
  orientation?: 'PORTRAIT' | 'LANDSCAPE' | null;
  onOrientation?: (value: 'PORTRAIT' | 'LANDSCAPE') => void;
  onRemovePanels?: () => void;
  /** Roof actions › Clear roof panels: removes every panel on the selected roof. */
  onClearRoofPanels?: () => void;
  /**
   * The rotate control's "Align longest edge": aligns the selected roof's
   * longest edge with the main roof and carries its panels with it.
   */
  onAlignLongestEdge?: () => void;
  onToggleRoofIncluded?: () => void;
  roofExcluded?: boolean;
  onDeleteRoof?: () => void;
  layoutPending?: boolean;
  /** Visible label of the scoped Repack, e.g. "Repack roof 1". */
  repackLabel?: string;
  repackDisabledReason?: string;
  onMode: (mode: CanvasMode) => void;
  onRepack: () => void;
  onAngle: (value: number) => void;
  onBegin: (id: number) => void;
  onEnd: (id: number) => void;
  onApply: () => void;
  onCancel: () => void;
}> = (props) => {
  const methods = useForm({ values: { angle: String(props.angle) } });
  const previousMode = useRef(props.mode);
  const triggers = useRef<Partial<Record<CanvasMode, HTMLButtonElement>>>({});
  const [restoreFocus, setRestoreFocus] = useState<CanvasMode | null>(null);
  const completionFocus = useRef<Element | null>(null);
  const completionIdentity = useRef(props.focusIdentity);
  useEffect(() => {
    if (previousMode.current !== 'inspect' && props.mode === 'inspect') {
      completionFocus.current = document.activeElement;
      completionIdentity.current = props.focusIdentity;
      setRestoreFocus(previousMode.current);
    }
    previousMode.current = props.mode;
  }, [props.mode, props.focusIdentity]);
  useEffect(() => {
    if (!restoreFocus) return;
    const relinquishFocus = (event: FocusEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target !== document.body &&
        target !== triggers.current[restoreFocus] &&
        !target.closest('[inert]')
      ) {
        setRestoreFocus(null);
      }
    };
    // A completion request is not authority to override a subsequent user
    // choice (Tariff, Equipment, header actions, or another page).
    document.addEventListener('focusin', relinquishFocus);
    return () => document.removeEventListener('focusin', relinquishFocus);
  }, [restoreFocus]);
  useEffect(() => {
    if (!restoreFocus) return;
    if (
      completionIdentity.current !== props.focusIdentity ||
      props.mode !== 'inspect'
    ) {
      setRestoreFocus(null);
      return;
    }
    if (props.focusEnabled === false) return;
    // Completion can refresh recommendations and temporarily make the entire
    // workspace inert. Focus the current mounted trigger after that settles.
    const frame = requestAnimationFrame(() => {
      const target = triggers.current[restoreFocus];
      if (!target?.isConnected || target.closest('[inert]')) return;
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        active !== document.body &&
        active !== target &&
        active !== completionFocus.current
      ) {
        setRestoreFocus(null);
        return;
      }
      target.focus({ preventScroll: true });
      if (document.activeElement === target) setRestoreFocus(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [restoreFocus, props.mode, props.focusEnabled, props.focusIdentity]);
  const rotating = props.mode === 'rotate' || props.pending;
  const closeReferenceAngle = () => props.onMode('inspect');
  const completeAngle = props.referenceAngle
    ? closeReferenceAngle
    : props.onApply;
  const dismissAngle = props.referenceAngle
    ? closeReferenceAngle
    : props.onCancel;
  const modeLabels: Record<CanvasMode, string> = {
    inspect: 'Inspecting',
    rotate: 'Rotate array',
  };
  const hasTarget = props.hasTarget ?? true;
  const roofActions = (
    <Popover className="relative">
      <PopoverButton
        as={Button}
        size="sm"
        variant="secondary"
        className="min-h-11 px-2 sm:min-h-0"
        aria-label="Roof actions"
      >
        Roof actions
        <UntitledIcon name="chevron-down" className="size-4" />
      </PopoverButton>
      <PopoverPanel
        role="region"
        aria-label="Roof actions menu"
        anchor={{ to: 'bottom end', gap: 4 }}
        portal
        focus
        // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Viewport-relative size
        className="z-popover max-h-[calc(100dvh-2rem)] w-60 space-y-1 overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white p-2 shadow-md"
        onKeyDown={(event) => {
          if (event.key === 'Escape') event.stopPropagation();
        }}
      >
        {({ close }) => (
          <>
            {props.onOrientation &&
              (['PORTRAIT', 'LANDSCAPE'] as const).map((value) => (
                <Button
                  key={value}
                  className="w-full min-h-11 justify-start"
                  size="sm"
                  variant="quaternary"
                  aria-label={`Canvas ${value.toLowerCase()}`}
                  aria-pressed={props.orientation === value}
                  onClick={() => {
                    props.onOrientation?.(value);
                    close();
                  }}
                >
                  {value === 'PORTRAIT' ? 'Portrait' : 'Landscape'}
                </Button>
              ))}
            {props.onClearRoofPanels &&
              !props.panelsSelected &&
              !props.roofExcluded && (
                <Button
                  className="w-full min-h-11 justify-start"
                  size="sm"
                  variant="quaternary"
                  colour="negative"
                  onClick={() => {
                    props.onClearRoofPanels?.();
                    close();
                  }}
                >
                  Clear roof panels
                </Button>
              )}
            {props.onToggleRoofIncluded && (
              <Button
                className="w-full min-h-11 justify-start"
                size="sm"
                variant="quaternary"
                onClick={() => {
                  props.onToggleRoofIncluded?.();
                  close();
                }}
              >
                {props.roofExcluded ? 'Include roof' : 'Exclude roof'}
              </Button>
            )}
            {props.onDeleteRoof && (
              <Button
                className="w-full min-h-11 justify-start"
                size="sm"
                variant="quaternary"
                colour="negative"
                onClick={() => {
                  props.onDeleteRoof?.();
                  close();
                }}
              >
                Delete roof
              </Button>
            )}
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
  const repack = (
    <Tooltip
      tooltipContent={
        props.repackDisabledReason ??
        (hasTarget ? 'Repack the selected roof only' : 'Repack all roofs')
      }
    >
      <Button
        className="min-h-11 px-2 sm:min-h-0"
        size="sm"
        variant="secondary"
        // Focusable while unavailable, so its tooltip reason can be read.
        aria-disabled={!!props.repackDisabledReason}
        onClick={() => {
          if (!props.repackDisabledReason) props.onRepack();
        }}
      >
        {props.repackLabel ?? 'Repack'}
      </Button>
    </Tooltip>
  );
  return (
    <section
      aria-label="Canvas editing"
      className={
        rotating
          ? 'w-full max-w-full space-y-1 rounded-lg border border-gray-200 bg-white p-2 shadow-sm sm:w-72 sm:space-y-2'
          : 'w-fit max-w-full space-y-1'
      }
    >
      {rotating ? (
        <Form
          methods={methods}
          onSubmit={completeAngle}
          className="space-y-1 sm:space-y-2"
        >
          <Controller
            control={methods.control}
            name="angle"
            render={({ field }) => (
              <Field
                name={field.name}
                id="canvas-array-angle"
                className="flex items-center justify-between gap-2"
              >
                <Field.Label className="min-w-0">
                  <span className="block truncate" title={props.scope}>
                    {props.scope}
                  </span>
                  <span className="block">
                    {props.referenceAngle
                      ? 'Reference angle (°)'
                      : 'Array angle (°)'}
                  </span>
                </Field.Label>
                <InputGroup sizeVariant="sm" className="w-20 shrink-0">
                  <InputGroup.RawInput
                    {...field}
                    id="canvas-array-angle"
                    aria-label={
                      props.referenceAngle
                        ? 'Reference angle for Repack (degrees)'
                        : 'Canvas array angle (degrees)'
                    }
                    inputMode="decimal"
                    inputClassName="min-h-11 px-2 py-1 sm:min-h-0"
                    onChange={(event) => {
                      field.onChange(event.target.value);
                      const value = Number(event.target.value);
                      if (
                        event.target.value.trim() &&
                        Number.isFinite(value) &&
                        value >= -45 &&
                        value <= 45
                      )
                        props.onAngle(value);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        completeAngle();
                      }
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        dismissAngle();
                      }
                    }}
                  />
                </InputGroup>
              </Field>
            )}
          />
          <input
            type="range"
            aria-label={
              props.referenceAngle
                ? 'Reference angle for Repack'
                : 'Canvas array rotation'
            }
            min={-45}
            max={45}
            step={1}
            value={props.angle}
            className="block w-full accent-primary"
            onChange={(event) => props.onAngle(Number(event.target.value))}
            onPointerDown={(event) => {
              if (event.button !== 0 || props.referenceAngle) return;
              props.onBegin(event.pointerId);
              event.currentTarget.setPointerCapture?.(event.pointerId);
            }}
            onPointerUp={(event) => {
              if (!props.referenceAngle) props.onEnd(event.pointerId);
            }}
            onPointerCancel={(event) => {
              if (!props.referenceAngle) props.onEnd(event.pointerId);
            }}
            onLostPointerCapture={(event) => {
              if (!props.referenceAngle) props.onEnd(event.pointerId);
            }}
            onKeyUp={(event) => {
              if (props.referenceAngle) {
                if (event.key === 'Enter') closeReferenceAngle();
                return;
              }
              if (
                [
                  'ArrowLeft',
                  'ArrowRight',
                  'ArrowUp',
                  'ArrowDown',
                  'Home',
                  'End',
                  'PageUp',
                  'PageDown',
                  'Enter',
                ].includes(event.key)
              )
                completeAngle();
            }}
          />
          <div
            role="group"
            aria-label="Angle completion"
            className="flex flex-wrap justify-end gap-1"
          >
            {props.referenceAngle ? (
              <>
                <Button
                  size="sm"
                  variant="quaternary"
                  onClick={closeReferenceAngle}
                  aria-label="Close angle editor"
                  className="min-h-11 sm:min-h-0"
                >
                  Close
                </Button>
                <Button
                  className="min-h-11 sm:min-h-0"
                  size="sm"
                  variant="primary"
                  aria-label="Repack target roof"
                  onClick={props.onRepack}
                >
                  Repack
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="quaternary"
                  aria-label="Cancel canvas rotation"
                  onClick={props.onCancel}
                  className="min-h-11 sm:min-h-0"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  aria-label="Apply canvas rotation"
                  onClick={props.onApply}
                  className="min-h-11 sm:min-h-0"
                >
                  Apply
                </Button>
              </>
            )}
          </div>
          <div className="sr-only sm:not-sr-only">
            {props.referenceAngle ? null : (
              <Text level="supporting">
                {props.pending
                  ? 'Preview only until Apply.'
                  : 'Change the angle to preview.'}
              </Text>
            )}
          </div>
        </Form>
      ) : (
        <div className="flex max-w-full flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-1.5 shadow-sm">
          {props.scopeControl ? (
            <div className="w-56 min-w-0 max-w-full sm:w-64">
              {props.scopeControl}
            </div>
          ) : (
            <div
              className="relative max-w-48 truncate px-1"
              title={props.scope}
            >
              <Text as="span">
                <strong>{props.scope}</strong>
                <span className="sr-only"> · {modeLabels[props.mode]}</span>
              </Text>
            </div>
          )}
          <div
            role="separator"
            aria-orientation="vertical"
            className="w-px self-stretch bg-gray-200"
          />
          {repack}
          {hasTarget && (
            <>
              {(!props.panelsSelected || props.onOrientation) && (
                <div
                  role="group"
                  aria-label="Rotate"
                  className="flex items-center gap-px"
                >
                  <Button
                    className={`min-h-11 px-2 sm:min-h-0 ${props.onAlignLongestEdge && !props.panelsSelected ? 'rounded-r-none' : ''}`}
                    size="sm"
                    variant="secondary"
                    onClick={() => props.onMode('rotate')}
                    ref={(node) => {
                      if (node) triggers.current.rotate = node;
                    }}
                  >
                    Rotate array
                  </Button>
                  {props.onAlignLongestEdge && !props.panelsSelected && (
                    <Tooltip tooltipContent="Align longest edge">
                      <Button
                        className="min-h-11 rounded-l-none px-2 sm:min-h-0"
                        size="sm"
                        variant="secondary"
                        aria-label="Align longest edge"
                        onClick={props.onAlignLongestEdge}
                      >
                        <UntitledIcon name="refresh-cw-01" className="size-4" />
                      </Button>
                    </Tooltip>
                  )}
                </div>
              )}
              {props.onRemovePanels && (
                <Button
                  className="min-h-11 sm:min-h-0"
                  size="sm"
                  variant="secondary"
                  aria-label="Remove selected panels"
                  onClick={props.onRemovePanels}
                >
                  Remove
                </Button>
              )}
              {roofActions}
            </>
          )}
        </div>
      )}
      {!rotating && props.layoutPending && (
        <div role="status" className="w-fit">
          <Button
            size="sm"
            variant="secondary"
            className="min-h-11 px-2 sm:min-h-0"
            disabled={!!props.repackDisabledReason}
            onClick={props.onRepack}
          >
            Changes pending · Repack
          </Button>
        </div>
      )}
    </section>
  );
};
export default CanvasEditingBar;
